export function omitNull(obj: Record<any, any>): any {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== null));
}

export function omitUndefined(obj: Record<any, any>): any {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));
}

export function camelCase(str: string): string {
  return str
    .replace(/_/g, ' ')
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '');
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}m`.replace('.0m', 'm');
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`.replace('.0k', 'k');
  }
  return num.toLocaleString();
}

export function formatNumberWithCommas(num: number): string {
  return num.toLocaleString();
}

export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * Picks properties from an object based on a list of keys.
 * @param obj works with objects, similar to lodash's pick, but there is no support for arrays
 * @param keys Array of keys to pick from the object. Supports nested keys (e.g., 'a.b.c').
 */
export function pick<T extends object>(
  obj: T | null | undefined,
  keys: string[]
): Record<string, unknown> {
  if (!obj) return {};

  const result: Record<string, unknown> = {};

  keys.forEach((key) => {
    const path = key.split('.');
    let src: unknown = obj;
    let dest: Record<string, unknown> = result;
    path.forEach((part, idx) => {
      if (typeof src !== 'object' || src === null || !Object.hasOwn(src, part)) {
        return;
      }
      if (idx === path.length - 1) {
        dest[part] = (src as Record<string, unknown>)[part];
      } else {
        if (typeof dest[part] !== 'object' || dest[part] === null) {
          dest[part] = {};
        }
        dest = dest[part] as Record<string, unknown>;
        src = (src as Record<string, unknown>)[part];
      }
    });
  });

  return result;
}
